import React from 'react'
import { Img } from 'react-image'
import styled from '@emotion/styled'
import { colors, Text } from 'ic-snacks'
import ImageActionsOverlay from './ImageActionsOverlay'
import ImageUploaderModal from './ImageUploaderModal'

interface ImageContainerProps {
  disabled?: boolean
  error?: boolean
}

interface ImageErrorProps {
  hasSiblingLabel?: boolean
}

const Container = styled.div`
  margin-bottom: 20px;
  width: 260px;
  text-align: center;
`

const ImageContainer = styled.div<ImageContainerProps>`
  position: relative;
  height: 260px;
  width: 260px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-color: ${({ error }) => (error ? colors.RED_700 : colors.GRAY_74)};
  border-width: 1px;
  border-style: ${({ disabled }) => (disabled ? 'dashed' : 'solid')};
  border-radius: 4px;
  overflow: hidden;
  background-color: ${({ disabled }) => (disabled ? colors.GRAY_93 : 'none')};
  cursor: ${({ disabled }) => (disabled ? 'no-drop' : 'auto')};
`

const Image = styled(Img)`
  margin: 0 auto;
  border-radius: 3px;
  width: 200px;
  height: 200px;
  object-fit: contain;
`

const ImageLabel = styled.span`
  display: inline-block;
  margin-top: 20px;
  color: ${colors.GRAY_46};
`

const ImageError = styled.span<ImageErrorProps>`
  display: inline-block;
  margin-top: ${({ hasSiblingLabel }) => (hasSiblingLabel ? '5px' : '15px')};
  color: ${colors.RED_700};
`

interface Props {
  onChange(imageUrl: string): void
  imageUrl?: string | null
  required?: boolean
  disabled?: boolean
  error?: string
  label?: string
  onDelete?: () => void
  validateField?: () => void
}

interface State {
  showModal: boolean
}

class ProductImageUploader extends React.Component<Props, State> {
  readonly state: State = {
    showModal: false,
  }

  handleChange = (imageUrl: string) => {
    this.handleCloseModal()
    this.props.onChange(imageUrl)
    this.props.validateField && this.props.validateField()
  }

  handleCloseModal = () => {
    this.setState({
      showModal: false,
    })
  }

  handleOpenModal = () => {
    this.setState({
      showModal: true,
    })
  }

  render() {
    const { imageUrl, onDelete, disabled, label, error } = this.props
    const { showModal } = this.state

    return (
      <Container>
        <ImageContainer disabled={disabled} error={!!error}>
          {imageUrl && <Image src={imageUrl} />}
          {!disabled && (
            <>
              <ImageActionsOverlay
                hasImage={!!imageUrl}
                onEdit={this.handleOpenModal}
                onDelete={onDelete}
              />
              <ImageUploaderModal
                isOpen={showModal}
                onChange={this.handleChange}
                onClose={this.handleCloseModal}
              />
            </>
          )}
        </ImageContainer>
        {label && (
          <Text variant="T.14">
            <ImageLabel>{label}</ImageLabel>
          </Text>
        )}
        {error && (
          <Text variant="T.12">
            <ImageError hasSiblingLabel={!!label}>{error}</ImageError>
          </Text>
        )}
      </Container>
    )
  }
}

export default ProductImageUploader
